import { useEffect, useState } from 'react'
import { FolderOpen, FolderPlus } from 'lucide-react'

import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { formatDate } from '@/lib/format'

import { Choice } from './WinnerCustomerBlock'
import { getClientLastProjects, type WinnerImportAddress, type WinnerImportProjectDecision, type WinnerImportPreview } from './winner-import.api'

type ExistingProject = { projectId: string; name: string | null; reference: string | null; when: string | null }

type Props = {
  preview: WinnerImportPreview
  /** Set when the user attached the import to an existing customer: their projects become choices. */
  customerId: string | null
  decision: WinnerImportProjectDecision
  onChange: (decision: WinnerImportProjectDecision) => void
}

const EMPTY_ADDRESS: WinnerImportAddress = { street: '', number: '', postalCode: '', city: '', country: 'BE' }

export function WinnerProjectBlock({ preview, customerId, decision, onChange }: Props) {
  const [fetched, setFetched] = useState<{ customerId: string; items: ExistingProject[] } | null>(null)
  const set = (patch: Partial<WinnerImportProjectDecision>) => onChange({ ...decision, ...patch })
  const address = decision.jobSiteAddress ?? EMPTY_ADDRESS
  const setAddress = (patch: Partial<WinnerImportAddress>) => set({ jobSiteAddress: { ...address, ...patch } })
  const candidate = preview.customerCandidates.find((c) => c.customerId === customerId)

  // A candidate's projects came with the preview; a customer picked through search needs one fetch.
  useEffect(() => {
    if (!customerId || candidate) return
    let alive = true
    getClientLastProjects(customerId)
      .then((items) => {
        if (alive) setFetched({ customerId, items: items.map((p) => ({ projectId: p.projectId, name: p.name, reference: p.reference, when: p.updatedAt })) })
      })
      .catch(() => undefined)
    return () => {
      alive = false
    }
  }, [customerId, candidate])

  const projects: ExistingProject[] = !customerId
    ? []
    : candidate
      ? candidate.projects.map((p) => ({ projectId: p.projectId, name: p.name, reference: p.reference, when: p.createdDate }))
      : fetched?.customerId === customerId ? fetched.items : []

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Winner range ce devis dans le projet <span className="font-medium text-foreground">{preview.project?.winnerName ?? '—'}</span>
        {preview.project?.winnerNumber && <> (n° {preview.project.winnerNumber})</>}
        {preview.project?.room && <> · pièce « {preview.project.room} »</>}.
      </p>

      <div className="grid gap-2" role="radiogroup" aria-label="Chantier">
        <Choice
          testId="winner-import-project-new"
          selected={decision.mode === 'new'}
          onSelect={() => set({ mode: 'new', projectId: undefined })}
          icon={<FolderPlus className="h-4 w-4" />}
          title="Créer un nouveau chantier"
          detail={decision.name || preview.project?.suggestedName || ''}
          badges={[]}
        />
        {projects.map((p) => (
          <Choice
            key={p.projectId}
            testId={`winner-import-project-existing-${p.projectId}`}
            selected={decision.mode === 'existing' && decision.projectId === p.projectId}
            onSelect={() => set({ mode: 'existing', projectId: p.projectId })}
            icon={<FolderOpen className="h-4 w-4" />}
            title={`Ajouter à « ${p.name ?? p.reference ?? 'Chantier'} »`}
            detail={[p.reference, p.when ? `depuis le ${formatDate(p.when)}` : null].filter(Boolean).join(' · ')}
            badges={[]}
          />
        ))}
      </div>

      {decision.mode === 'new' && (
        <div className="grid gap-3 rounded-lg border bg-slate-50/60 p-4 sm:grid-cols-6">
          <div className="space-y-1 sm:col-span-4">
            <Label htmlFor="winner-import-project-name" className="text-xs">Nom du chantier</Label>
            <Input id="winner-import-project-name" data-testid="winner-import-project-name-input" value={decision.name} onChange={(e) => set({ name: e.target.value })} />
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label className="text-xs">Type</Label>
            <Select value={decision.projectTypeId ?? undefined} onValueChange={(v) => set({ projectTypeId: v })}>
              <SelectTrigger data-testid="winner-import-project-type-select" className="w-full bg-background">
                <SelectValue placeholder="Choisir" />
              </SelectTrigger>
              <SelectContent>
                {preview.projectTypes.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1 sm:col-span-4">
            <Label htmlFor="winner-import-project-street" className="flex justify-between text-xs">
              Adresse du chantier
              {!preview.project?.address && <span className="font-normal text-muted-foreground">absente de Winner — facultatif</span>}
            </Label>
            <Input id="winner-import-project-street" data-testid="winner-import-project-street-input" placeholder="Rue" value={address.street} onChange={(e) => setAddress({ street: e.target.value })} />
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor="winner-import-project-number" className="text-xs">N°</Label>
            <Input id="winner-import-project-number" data-testid="winner-import-project-number-input" value={address.number} onChange={(e) => setAddress({ number: e.target.value })} />
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor="winner-import-project-zip" className="text-xs">Code postal</Label>
            <Input id="winner-import-project-zip" data-testid="winner-import-project-zip-input" value={address.postalCode} onChange={(e) => setAddress({ postalCode: e.target.value })} />
          </div>
          <div className="space-y-1 sm:col-span-4">
            <Label htmlFor="winner-import-project-city" className="text-xs">Localité</Label>
            <Input id="winner-import-project-city" data-testid="winner-import-project-city-input" value={address.city} onChange={(e) => setAddress({ city: e.target.value })} />
          </div>
        </div>
      )}
    </div>
  )
}
